"use client";

import Image from "next/image";
import Loader from "@/components/Loader";
import DOMPurify from "isomorphic-dompurify";

interface StartupItem {
  clientLogo: string;
  tagLine: string;
  smallOverview: string;
}

interface EmpoweringStartupsProps {
  startupData?: any;
  categoryData?: any;
  loading?: boolean;
  error?: string | null;
}

const ITEMS_PER_PAGE = 8;

import { useState } from "react";

export default function EmpoweringStartups({
  startupData = [],
  categoryData = null,
  loading,
  error = null,
}: EmpoweringStartupsProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.ceil(startupData.length / ITEMS_PER_PAGE);
  const hasMultiplePages = startupData.length > ITEMS_PER_PAGE;

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    const maxPage = Math.ceil(startupData.length / ITEMS_PER_PAGE) - 1;
    if (currentPage < maxPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const currentCards = startupData.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  if (startupData.length === 0) {
    return null;
  }

  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center w-full h-[60vh] py-20">
          <Loader size={80} />
        </div>
      ) : error ? (
        <div className="text-center py-32">
          <p className="text-xl md:text-2xl text-red-600 font-medium">
            {error}
          </p>
        </div>
      ) : startupData.length > 0 ? (
        <section className="w-full bg-[#d9e2ed] py-[60px] md:py-[100px] lg:py-[150px] px-4 md:px-8 lg:px-[100px]">
          <div className="max-w-[5000px] mx-auto">
            <div className="flex flex-col lg:flex-row items-start justify-between mb-[40px] md:mb-[60px] lg:mb-[84px] gap-[30px] lg:gap-0">
              <div className="w-full lg:w-[1283px] flex flex-col md:flex-row gap-[20px] md:gap-[30px]">
                <div className="w-full md:w-[116px] flex items-start">
                  <p className="text-[#00040b] text-[14px] md:text-[16px] font-medium uppercase">
                    {categoryData?.title}
                  </p>
                </div>

                <div className="flex-1 flex flex-col gap-[12px] md:gap-[15px]">
                  <h2 className="text-[32px] md:text-[48px] lg:text-[length:var(--section-title)] font-medium clash-font text-[#00040b] leading-[120%]">
                    Built for Startups
                  </h2>
                  <p className="text-[16px] md:text-[20px] lg:text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] font-normal text-[#636364] leading-[150%] lg:leading-[160%] whitespace-pre-line">
                    {categoryData?.descriptions}
                  </p>
                </div>
              </div>

              {hasMultiplePages && (
                <div className="flex items-center gap-[8px] self-end lg:self-auto">
                  <button
                    onClick={handlePrevious}
                    aria-label="Previous slide"
                    disabled={currentPage === 0}
                    className="w-[32px] h-[32px] sm:w-[40px] sm:h-[40px] rounded-full border border-[#ecf0f6] bg-white flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M12.5 15L7.5 10L12.5 5"
                        stroke="#00040b"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={handleNext}
                    aria-label="Next slide"
                    disabled={currentPage === totalPages - 1}
                    className="w-[32px] h-[32px] sm:w-[40px] sm:h-[40px] rounded-full bg-[#225bd4] flex items-center justify-center hover:bg-[#1a4aa8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M7.5 5L12.5 10L7.5 15"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 [@media(min-width:1500px)]:grid-cols-4 gap-0">
              {currentCards?.map((startup: StartupItem, index: number) => (
                <div
                  key={`${startup.clientLogo || "startup"}-${index}`}
                  className="[@media(min-width:1500px)]:h-[290px] py-[30px] lg:py-[0px] lg:h-[350px] bg-white border border-[#ecf0f6] flex flex-col items-center justify-center px-[30px] md:px-[35px] lg:px-[40px] text-center"
                >
                  <div className="flex items-center justify-center h-[45px] md:h-[50px] lg:h-[53px] mb-[8px] md:mb-[10px]">
                    <Image
                      src={startup.clientLogo}
                      alt={startup.tagLine}
                      width={208}
                      height={53}
                      className="max-w-[150px] md:max-w-[180px] lg:max-w-[208px] max-h-[45px] md:max-h-[50px] lg:max-h-[53px] w-auto h-auto object-contain"
                      unoptimized
                      onError={(e: any) => {
                        e.currentTarget = "/fallback-logo.png";
                      }}
                    />
                  </div>

                  <h3 className="text-[#00040b] text-[16px] md:text-[17px] lg:text-[18px] font-semibold clash-font mb-[8px] md:mb-[10px]">
                    {startup.tagLine}
                  </h3>

                  <p
                    className="text-[#636364] text-[14px] md:text-[15px] lg:text-[16px] font-medium italic leading-[140%]"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        (startup?.smallOverview || "")
                          .replace(/\n/g, "<br>")
                          .replace(/<b>(.*?)<\/b>/gi, "<strong>$1</strong>")
                          .replace(/<i>(.*?)<\/i>/gi, "<em>$1</em>"),
                      ),
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <div className="text-center py-32">
          <p className="text-xl md:text-2xl text-gray-600">
            No trusted clients to display at the moment.
          </p>
        </div>
      )}
    </>
  );
}
